///
/// EarthBrowser
///
/// Created by Matt Giger on 2/3/2025
/// Copyright (c) 2025 Geodata Labs
///

import { hashString } from "@stovokor/utils/hashString"

const cSourceSeparator = '#version 300 es'

export class ShaderSourceDefinition {
	public isUrl: boolean
	public sourceId: string
	public _defines: Record<string, string>
	public identifier: string

	constructor(sourceId: string, defines: Record<string, string> = {}) {
		this.isUrl = false
		this.sourceId = sourceId
		this._defines = defines
		this.identifier = ShaderSourceDefinition.identify(sourceId, defines)
	}

	public get defines(): Record<string, string> {
		return this._defines
	}

	public set defines(defines: Record<string, string>) {
		this._defines = defines
		this.identifier = ShaderSourceDefinition.identify(this.sourceId, defines)
	}

	// Cache key for the compiled program. Values take part as well as names:
	// a define like GROUND_OVERLAY_COUNT changes the program's uniforms with
	// its value, so programs built with different values must not be shared
	private static identify(sourceId: string, defines: Record<string, string>): string {
		const pairs = Object.entries(defines).map(([name, value]) => `${name}=${value}`).sort()
		return hashString(`${sourceId}${pairs.join(",")}`)
	}
}

export function combineDefines(defines: Record<string, string>[]): Record<string, string> {
	const combined: Record<string, string> = {}
	for(const define of defines) {
		for(const [key, value] of Object.entries(define)) {
			combined[key] = value
		}
	}
	return combined
}

export class ShaderSourceCode {
	public vertex: string = cVertexDefault
	public fragment: string = cFragmentDefault
	public includes: string[] = []
	public defines: Record<string, string> = {}

	constructor(source: string = '', defines: Record<string, string> = {}) {
		// The caller's defines, before resolveDefines merges the source's own
		// #define lines into the same map
		const supplied = Object.entries(defines)
		this.defines = defines
		this.resolveDefines(source)
		source = this.resolveConditionals(source)
		this.extractIncludes(source)

		const vertexStart = source.indexOf(cSourceSeparator)
		const fragmentStart = source.lastIndexOf(cSourceSeparator)
		if(fragmentStart === -1) {
			this.vertex = cVertexDefault
			this.fragment = source
		} else if(vertexStart !== -1) {
			this.vertex = source.substring(vertexStart, fragmentStart)
			this.fragment = source.substring(fragmentStart)
		}

		// The #ifdef/#if resolution above only covers bare names in the main
		// source. Included chunks and value tests (`#if GROUND_OVERLAY_COUNT > 1`)
		// are left to the GLSL preprocessor, which needs the macros to exist:
		// emit them after #version in both stages. Names the source already
		// #defines are skipped so nothing is redefined
		const emitted = supplied
			.filter(([name]) => !new RegExp(`#define\\s+${name}\\b`).test(source))
			.map(([name, value]) => `#define ${name} ${value}`)
		if(emitted.length > 0) {
			const block = emitted.join('\n') + '\n'
			this.vertex = ShaderSourceCode.insertAfterVersion(this.vertex, block)
			this.fragment = ShaderSourceCode.insertAfterVersion(this.fragment, block)
		}
	}

	/** Place `block` on the line after the #version directive, which must stay first */
	private static insertAfterVersion(stage: string, block: string): string {
		const version = stage.indexOf(cSourceSeparator)
		if(version === -1) {
			return block + stage
		}
		const lineEnd = stage.indexOf('\n', version)
		if(lineEnd === -1) {
			return stage + '\n' + block
		}
		return stage.substring(0, lineEnd + 1) + block + stage.substring(lineEnd + 1)
	}

	public applyInclude(include: string, contents: string) {
		const regex = new RegExp(`#include\\s+["'<]${include}[">']`, 'g')
		for(const match of Array.from(this.vertex.matchAll(regex))) {
			this.vertex = this.vertex.replace(match[0], contents)
		}
		for(const match of Array.from(this.fragment.matchAll(regex))) {
			this.fragment = this.fragment.replace(match[0], contents)
		}
	}

	/**
	 * Resolve `#ifdef NAME` and `#if NAME` blocks (with optional `#else`)
	 * against the known defines in one pass over the lines, keeping a stack
	 * for nesting. Directives this doesn't evaluate (`#ifndef`, `#if` with
	 * an expression such as `GROUND_OVERLAY_COUNT > 1`, `#elif`) are passed
	 * through verbatim for the GLSL preprocessor, which sees the same
	 * defines emitted after #version; their nesting is still tracked so the
	 * matching `#else`/`#endif` lines stay with them.
	 */
	private resolveConditionals(source: string): string {
		interface Frame {
			handled: boolean		// evaluated here (lines dropped) vs passed through
			parentEmitting: boolean
			emitting: boolean
			taken: boolean			// a branch of a handled block has already been chosen
		}
		const lines = source.split('\n')
		const out: string[] = []
		const stack: Frame[] = []
		const emitting = (): boolean => stack.length === 0 || stack[stack.length - 1].emitting

		for(const line of lines) {
			const trimmed = line.trim()
			if(trimmed.startsWith('#ifdef ') || trimmed.startsWith('#if ')) {
				const isIfdef = trimmed.startsWith('#ifdef ')
				const name = trimmed.substring(isIfdef ? 7 : 4).trim()
				const parentEmitting = emitting()
				if(/^[A-Za-z_]\w*$/.test(name)) {
					const condition = isIfdef ? this.defines[name] !== undefined : this.isTruthy(this.defines[name])
					stack.push({ handled: true, parentEmitting, emitting: parentEmitting && condition, taken: condition })
				} else {
					stack.push({ handled: false, parentEmitting, emitting: parentEmitting, taken: false })
					if(parentEmitting) out.push(line)
				}
			} else if(trimmed.startsWith('#ifndef')) {
				const parentEmitting = emitting()
				stack.push({ handled: false, parentEmitting, emitting: parentEmitting, taken: false })
				if(parentEmitting) out.push(line)
			} else if(trimmed === '#else' || trimmed.startsWith('#else ') || trimmed.startsWith('#elif')) {
				const top = stack[stack.length - 1]
				if(top === undefined) {
					out.push(line)			// unbalanced; leave it for the GLSL compiler to report
				} else if(top.handled) {
					if(trimmed.startsWith('#elif')) {
						const name = trimmed.substring(5).trim()
						const condition = /^[A-Za-z_]\w*$/.test(name) && this.isTruthy(this.defines[name])
						top.emitting = top.parentEmitting && !top.taken && condition
						top.taken = top.taken || condition
					} else {
						top.emitting = top.parentEmitting && !top.taken
						top.taken = true
					}
				} else if(top.parentEmitting) {
					out.push(line)
				}
			} else if(trimmed === '#endif' || trimmed.startsWith('#endif ')) {
				const top = stack.pop()
				if(top === undefined || (!top.handled && top.parentEmitting)) {
					out.push(line)
				}
			} else if(emitting()) {
				out.push(line)
			}
		}

		if(stack.length > 0) {
			console.warn('[ShaderSourceCode] Unterminated conditional block in shader source')
		}
		return out.join('\n')
	}

	private isTruthy(value: string | undefined): boolean {
		return value !== undefined && value !== '0' && value !== 'false' && value !== '0.0'
	}

	private resolveDefines(source: string): void {
		const regex = /#define\s+(\w+)\s+(.*)/g
		for(const match of Array.from(source.matchAll(regex))) {
			const name = match[1]
			const value = match[2]
			this.defines[name] = value
		}
	}

	private extractIncludes(source: string): void {
		const regex = /#include\s+["'<](.*?)["'>]/g
		for(const match of Array.from(source.matchAll(regex))) {
			this.includes.push(match[1])
		}
	}
}

const cVertexDefault = `#version 300 es
in highp vec2 position;
uniform highp mat4 modelViewProjection;
void main() {
	gl_Position = modelViewProjection * vec4(position, 0., 1.);
}`

const cFragmentDefault = `#version 300 es
precision highp float;
out vec4 fragColor;
void main() {
	fragColor = vec4(0., 0., 1., 1.);
}`

export class ShaderSourceError {
	public static parse(source: string, errorString: string): ShaderSourceError[] {
		return errorString.split('\n').map(error => {
			const sections = error.split(':')
			if(sections.length < 4) {
				return undefined
			}
			const line = parseInt(sections[2])
			const message = sections[3] + ':' + sections[4]
			const firstSingleQuote = message.indexOf("'")
			const secondSingleQuote = message.indexOf("'", firstSingleQuote + 1)
			let startColumn = 0
			let endColumn = 1000
			if(firstSingleQuote !== -1 && secondSingleQuote !== -1) {
				const searchFor = message.substring(firstSingleQuote + 1, secondSingleQuote)
				// Link logs are parsed with no source, and a reported line
				// may fall outside it; neither should throw
				const contentLine = source.split('\n')[line - 1]
				const start = contentLine?.indexOf(searchFor) ?? -1
				if(start !== -1) {
					startColumn = start + 1
					endColumn = startColumn + searchFor.length + 1
				} else {
					startColumn = 0
					endColumn = 0
				}
			}
			return new ShaderSourceError(message, line, startColumn, endColumn)
		}).filter(error => error !== undefined)
	}

	constructor(public message: string, public line: number,
		public columnStart: number, public columnEnd: number) {}
}